import { useState } from 'react';
import { X, ChevronRight, ChevronLeft, MapPin, Users, Calendar, Clock, Map, Link as LinkIcon, Smartphone, CheckCircle, AlertTriangle } from 'lucide-react';

interface TutorialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const steps = [
  {
    title: "1. Les Points Opérationnels",
    icon: <MapPin className="w-10 h-10 text-indigo-400" />,
    color: "from-indigo-500/20 to-indigo-900/20",
    border: "border-indigo-500/30",
    textShadow: "text-indigo-400",
    description: "Tout commence par la création de vos points opérationnels (Parking, Accueil, Restaurant...).",
    details: "Allez dans l'onglet 'Configuration Points'. Définissez les heures d'ouverture, les besoins en effectifs (Team Requis) et l'emplacement exact. Ces points serviront de base pour placer votre équipe tout au long de la journée.",
    mockupType: "points"
  },
  {
    title: "2. Votre Équipe (Team)",
    icon: <Users className="w-10 h-10 text-emerald-400" />,
    color: "from-emerald-500/20 to-emerald-900/20",
    border: "border-emerald-500/30",
    textShadow: "text-emerald-400",
    description: "Ajoutez ensuite les membres de votre équipe.",
    details: "Dans 'Gestion du Team', ajoutez chaque personne en précisant son rôle principal (Cashless, Parking, Global...). Vous pouvez également gérer leurs catégories d'affectation pour simplifier le filtrage et l'organisation plus tard.",
    mockupType: "staff"
  },
  {
    title: "3. La Planification (Matrix)",
    icon: <Calendar className="w-10 h-10 text-amber-400" />,
    color: "from-amber-500/20 to-amber-900/20",
    border: "border-amber-500/30",
    textShadow: "text-amber-400",
    description: "C'est l'heure d'assigner vos équipes aux points.",
    details: "Utilisez la 'Planning Matrix'. Vous y verrez tous les points configurés. Cliquez sur le bouton '+' d'un créneau pour y assigner un membre du team disponible. La matrice vous alertera en rouge si un point manque de personnel par rapport à votre configuration.",
    mockupType: "matrix"
  },
  {
    title: "4. Vue Chronologique (Timeline)",
    icon: <Clock className="w-10 h-10 text-blue-400" />,
    color: "from-blue-500/20 to-blue-900/20",
    border: "border-blue-500/30",
    textShadow: "text-blue-400",
    description: "Suivez l'emploi du temps de chaque personne.",
    details: "L'onglet 'Team Timeline' offre un diagramme de Gantt interactif. Il vous permet de visualiser facilement qui fait quoi et à quelle heure, d'identifier les temps de pause, de repérer les repas manqués et de détecter visuellement les chevauchements de planning.",
    mockupType: "timeline"
  },
  {
    title: "5. La Carte (Live Map)",
    icon: <Map className="w-10 h-10 text-rose-400" />,
    color: "from-rose-500/20 to-rose-900/20",
    border: "border-rose-500/30",
    textShadow: "text-rose-400",
    description: "Visualisez le déploiement sur le terrain en temps réel.",
    details: "La 'Live Map' place vos points opérationnels sur une carte géographique. Vous pouvez déplacer librement vos marqueurs de points pour une précision maximale, et voir instantanément quel team y est assigné à l'instant T.",
    mockupType: "map"
  },
  {
    title: "6. Générer l'Accès Team",
    icon: <LinkIcon className="w-10 h-10 text-fuchsia-400" />,
    color: "from-fuchsia-500/20 to-fuchsia-900/20",
    border: "border-fuchsia-500/30",
    textShadow: "text-fuchsia-400",
    description: "Partagez l'application avec vos équipes.",
    details: "Depuis la 'Gestion du Team', cliquez sur l'icône de 'Clé' en haut à droite. Le système générera un lien unique et sécurisé pour chaque membre. Envoyez-le leur : ils n'auront besoin d'aucun mot de passe ni de téléchargement d'application pour se connecter !",
    mockupType: "link"
  },
  {
    title: "7. L'Application Mobile Team",
    icon: <Smartphone className="w-10 h-10 text-teal-400" />,
    color: "from-teal-500/20 to-teal-900/20",
    border: "border-teal-500/30",
    textShadow: "text-teal-400",
    description: "Un outil de terrain Premium.",
    details: "Le team accède à une interface mobile dédiée. Ils peuvent y voir leur Badge Numérique (QR Code), leur planning du jour, signaler des incidents en direct ou encore demander des remplacements.",
    mockupType: "mobile"
  }
];

export function TutorialModal({ isOpen, onClose }: TutorialModalProps) {
  const [currentStep, setCurrentStep] = useState(0);

  if (!isOpen) return null;

  const step = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(c => c + 1);
    else onClose();
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(c => c - 1);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-[#0A0B0E]/80 backdrop-blur-md transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-4xl bg-[#14161B] border border-[#2D3139] rounded-3xl shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col md:flex-row h-[85vh] md:h-[600px] animate-in fade-in zoom-in-95 duration-300">
        
        {/* Left Side: Navigation & Progress */}
        <div className="w-full md:w-1/3 bg-[#0A0B0E]/50 border-b md:border-b-0 md:border-r border-[#2D3139] p-6 flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-center mb-4 md:mb-8">
              <h2 className="text-xl font-bold text-white tracking-wide">Guide Rapide</h2>
              <button 
                onClick={onClose}
                className="md:hidden p-2 rounded-full bg-[#2D3139]/50 text-slate-400 hover:text-white hover:bg-[#2D3139] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Progress Indicator */}
            <div className="md:hidden flex items-center justify-between mb-4">
              <span className="text-sm font-bold text-slate-400">Étape {currentStep + 1} sur {steps.length}</span>
              <div className="flex gap-1">
                {steps.map((_, idx) => (
                  <div key={idx} className={`h-1.5 rounded-full ${currentStep === idx ? 'w-4 bg-indigo-500' : 'w-1.5 bg-[#2D3139]'} transition-all`}></div>
                ))}
              </div>
            </div>
            
            <div className="hidden md:block space-y-3">
              {steps.map((s, idx) => (
                <div 
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`px-4 py-3 rounded-xl cursor-pointer transition-all duration-300 border flex items-center gap-3 ${
                    currentStep === idx 
                      ? `bg-gradient-to-r ${s.color} border-l-4 border-l-[color:inherit] ${s.border}` 
                      : 'border-transparent hover:bg-[#1A1D23] text-slate-400'
                  }`}
                  style={{ borderLeftColor: currentStep === idx ? 'currentColor' : 'transparent', color: currentStep === idx ? '#fff' : '' }}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${currentStep === idx ? 'bg-white/20' : 'bg-[#2D3139]'}`}>
                    {idx + 1}
                  </div>
                  <span className={`text-sm font-semibold truncate ${currentStep === idx ? '' : ''}`}>
                    {s.title.substring(3)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden md:flex gap-4 mt-8">
            <button 
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="flex-1 bg-[#1A1D23] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2D3139] text-white py-3 rounded-xl flex justify-center transition-colors border border-[#2D3139]"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button 
              onClick={handleNext}
              className={`flex-[2] py-3 rounded-xl flex items-center justify-center gap-2 font-bold transition-all shadow-lg text-white
                ${currentStep === steps.length - 1 ? 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/20' : 'bg-indigo-600 hover:bg-indigo-500 shadow-indigo-500/20'}
              `}
            >
              {currentStep === steps.length - 1 ? 'Terminer' : 'Suivant'}
              {currentStep !== steps.length - 1 && <ChevronRight className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Right Side: Content */}
        <div className="w-full md:w-2/3 flex flex-col p-6 md:p-10 relative overflow-y-auto custom-scrollbar">
          <button 
            onClick={onClose}
            className="hidden md:block absolute top-6 right-6 p-2 rounded-full bg-[#1A1D23] text-slate-400 hover:text-white hover:bg-[#2D3139] transition-colors border border-[#2D3139]"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex-1 flex flex-col items-center justify-center animate-in slide-in-from-right-8 duration-500" key={currentStep}>
            
            <div className={`w-24 h-24 rounded-3xl bg-gradient-to-br ${step.color} border ${step.border} flex items-center justify-center mb-8 shadow-2xl`}>
              {step.icon}
            </div>

            <h3 className={`text-3xl font-bold text-center mb-4 ${step.textShadow}`}>
              {step.title}
            </h3>

            <p className="text-xl text-white text-center font-medium mb-6 leading-relaxed max-w-lg">
              {step.description}
            </p>

            <p className="text-slate-400 text-center leading-relaxed max-w-lg mb-8">
              {step.details}
            </p>

            {step.mockupType === 'points' && (
              <div className="w-full max-w-sm mx-auto mt-4 bg-[#14161B]/80 backdrop-blur-xl border border-[#2D3139] rounded-2xl p-4 shadow-xl ring-1 ring-[#2D3139]/50">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-sm">Parking Check-in</h4>
                      <p className="text-xs text-slate-400">Point Opérationnel</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#0A0B0E] rounded-xl p-3 border border-[#2D3139] flex justify-between items-center">
                  <div className="flex items-center gap-2 text-slate-300 text-sm">
                    <Clock className="w-4 h-4 text-slate-500" />
                    14:00 - 18:00
                  </div>
                  <div className="flex items-center gap-1 bg-[#1A1D23] px-2 py-1 rounded-md border border-[#2D3139]">
                    <Users className="w-3 h-3 text-emerald-400" />
                    <span className="text-xs text-emerald-400 font-bold">2 requis</span>
                  </div>
                </div>
              </div>
            )}

            {step.mockupType === 'staff' && (
              <div className="w-full max-w-sm mx-auto mt-4 space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="bg-[#14161B]/80 backdrop-blur-xl border border-[#2D3139] rounded-2xl p-4 shadow-xl flex items-center justify-between ring-1 ring-[#2D3139]/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold">
                        {i === 1 ? 'AB' : 'YS'}
                      </div>
                      <div>
                        <h4 className="text-white font-bold text-sm">{i === 1 ? 'Amen Ben Mlouka' : 'Youssef Seffen'}</h4>
                        <p className="text-xs text-slate-400">{i === 1 ? 'Global' : 'Parking Only'}</p>
                      </div>
                    </div>
                    <div className={`w-3 h-3 rounded-full ${i === 1 ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-[#2D3139]'}`}></div>
                  </div>
                ))}
              </div>
            )}

            {step.mockupType === 'matrix' && (
              <div className="w-full max-w-md mx-auto mt-4 bg-[#14161B]/80 backdrop-blur-xl border border-[#2D3139] rounded-2xl p-4 shadow-xl ring-1 ring-[#2D3139]/50 overflow-hidden">
                <div className="flex border-b border-[#2D3139] pb-2 mb-2">
                  <div className="w-1/3 text-xs text-slate-400 font-bold uppercase">Point</div>
                  <div className="w-1/3 text-xs text-slate-400 font-bold uppercase text-center">14:00</div>
                  <div className="w-1/3 text-xs text-slate-400 font-bold uppercase text-center">15:00</div>
                </div>
                <div className="flex items-center mb-2">
                  <div className="w-1/3 text-sm font-bold text-white truncate pr-2">Parking</div>
                  <div className="w-1/3 px-1">
                    <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-1.5 text-center text-xs text-emerald-400 font-bold truncate">Amen B.</div>
                  </div>
                  <div className="w-1/3 px-1">
                    <div className="bg-[#1A1D23] border border-[#2D3139] border-dashed rounded-lg p-1.5 text-center flex justify-center items-center cursor-pointer hover:border-indigo-500/50">
                      <div className="w-4 h-4 rounded bg-indigo-500/20 text-indigo-400 flex items-center justify-center font-bold leading-none">+</div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-1/3 text-sm font-bold text-white truncate pr-2">Accueil</div>
                  <div className="w-2/3 px-1">
                    <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-1.5 text-center text-xs text-rose-400 font-bold flex items-center justify-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Manquant
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step.mockupType === 'timeline' && (
              <div className="w-full max-w-md mx-auto mt-4 bg-[#14161B]/80 backdrop-blur-xl border border-[#2D3139] rounded-2xl p-4 shadow-xl ring-1 ring-[#2D3139]/50 overflow-hidden">
                <div className="flex border-b border-[#2D3139] pb-2 mb-3">
                  <div className="w-1/4"></div>
                  <div className="w-3/4 flex justify-between text-[10px] text-slate-400 font-bold">
                    <span>12h</span><span>14h</span><span>16h</span><span>18h</span>
                  </div>
                </div>
                {[
                  { name: 'Amen', color: 'bg-indigo-500', left: '20%', width: '50%' },
                  { name: 'Fadi', color: 'bg-emerald-500', left: '10%', width: '40%' },
                  { name: 'Youssef', color: 'bg-rose-500', left: '40%', width: '60%' }
                ].map((s, i) => (
                  <div key={i} className="flex items-center mb-3">
                    <div className="w-1/4 text-xs font-bold text-white truncate">{s.name}</div>
                    <div className="w-3/4 relative h-6 bg-[#0A0B0E] rounded-md border border-[#2D3139]">
                      <div className={`absolute top-0 bottom-0 ${s.color} rounded-md shadow-lg opacity-80`} style={{ left: s.left, width: s.width }}></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {step.mockupType === 'map' && (
              <div className="w-full max-w-md mx-auto mt-4 bg-[#0A0B0E] border border-[#2D3139] rounded-2xl h-40 shadow-xl ring-1 ring-[#2D3139]/50 overflow-hidden relative">
                {/* Grid pattern for map background */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                
                {/* Markers */}
                <div className="absolute top-1/3 left-1/4 animate-bounce">
                  <div className="w-8 h-8 bg-indigo-500/20 border-2 border-indigo-500 rounded-full flex items-center justify-center text-indigo-400 shadow-[0_0_15px_rgba(99,102,241,0.5)]">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div className="mt-1 bg-[#14161B]/90 backdrop-blur px-2 py-0.5 rounded text-[10px] font-bold text-white border border-[#2D3139] whitespace-nowrap -ml-2">Parking</div>
                </div>

                <div className="absolute top-1/2 right-1/3">
                  <div className="w-8 h-8 bg-emerald-500/20 border-2 border-emerald-500 rounded-full flex items-center justify-center text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.5)]">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div className="mt-1 bg-[#14161B]/90 backdrop-blur px-2 py-0.5 rounded text-[10px] font-bold text-white border border-[#2D3139] whitespace-nowrap -ml-2">Accueil</div>
                </div>
              </div>
            )}

            {step.mockupType === 'link' && (
              <div className="w-full max-w-md mx-auto mt-4 bg-[#14161B]/80 backdrop-blur-xl border border-[#2D3139] rounded-2xl p-5 shadow-xl ring-1 ring-[#2D3139]/50 flex flex-col items-center">
                <div className="w-12 h-12 bg-fuchsia-500/20 rounded-full border border-fuchsia-500/30 flex items-center justify-center text-fuchsia-400 mb-4 animate-pulse">
                  <LinkIcon className="w-6 h-6" />
                </div>
                <h4 className="text-white font-bold text-sm mb-2">Accès Sécurisé Généré</h4>
                <div className="flex w-full items-center gap-2">
                  <div className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-lg p-2 text-xs text-slate-400 font-mono truncate">
                    https://wfm-app.com/team/login?token=abc-123...
                  </div>
                  <div className="bg-[#1A1D23] hover:bg-fuchsia-500/20 border border-[#2D3139] hover:border-fuchsia-500/50 text-white px-3 py-2 rounded-lg text-xs font-bold cursor-pointer transition-colors">
                    Copier
                  </div>
                </div>
              </div>
            )}

            {step.mockupType === 'mobile' && (
              <div className="w-full max-w-xs mx-auto border-[6px] border-[#1A1D23] rounded-[2.5rem] overflow-hidden bg-[#0A0B0E] shadow-2xl relative h-[450px] shrink-0 mt-4 ring-1 ring-[#2D3139]">
                {/* iPhone Notch */}
                <div className="absolute top-0 inset-x-0 h-6 bg-[#1A1D23] rounded-b-2xl w-32 mx-auto z-20"></div>
                
                {/* Mockup Screen Content */}
                <div className="p-4 pt-8 h-full overflow-y-auto custom-scrollbar flex flex-col gap-4 bg-gradient-to-b from-teal-900/20 to-[#0A0B0E]">
                  {/* Header */}
                  <div className="flex justify-between items-center">
                    <div className="w-10 h-10 rounded-full bg-teal-500/20 border border-teal-500/30 flex items-center justify-center text-teal-400 font-bold">AB</div>
                    <div className="bg-[#14161B]/80 px-3 py-1 rounded-full border border-[#2D3139] text-[10px] text-emerald-400 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> En Service
                    </div>
                  </div>

                  {/* QR Badge */}
                  <div className="bg-gradient-to-br from-[#14161B] to-[#1A1D23] p-4 rounded-2xl border border-[#2D3139] flex flex-col items-center">
                    <p className="text-xs text-slate-400 mb-2 uppercase tracking-widest">Badge Team</p>
                    <div className="w-32 h-32 bg-white rounded-xl flex items-center justify-center mb-3">
                      <div className="w-24 h-24 bg-[repeating-conic-gradient(black_0%_25%,white_0%_50%)] bg-[length:10px_10px] opacity-80" />
                    </div>
                    <p className="font-bold text-white">Amen Ben Mlouka</p>
                  </div>

                  {/* Planning */}
                  <div className="space-y-2">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Aujourd'hui</p>
                    <div className="bg-[#14161B] border-l-2 border-indigo-500 p-3 rounded-xl flex items-center gap-3">
                      <div className="bg-[#1A1D23] p-2 rounded-lg text-indigo-400">
                        <MapPin className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-white">Parking Check-in</p>
                        <p className="text-[10px] text-slate-400">14:00 - 18:00</p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-2 mt-auto">
                    <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-xl flex flex-col items-center justify-center text-amber-400 gap-1">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="text-[10px] font-bold">Signaler</span>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 rounded-xl flex flex-col items-center justify-center text-emerald-400 gap-1">
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-[10px] font-bold">Check-out</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Bottom Navigation */}
          <div className="md:hidden flex gap-3 mt-8 pt-6 border-t border-[#2D3139] shrink-0">
            <button 
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="w-12 h-12 flex-shrink-0 bg-[#1A1D23] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl flex items-center justify-center transition-colors border border-[#2D3139]"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button 
              onClick={handleNext}
              className={`flex-1 h-12 rounded-xl flex items-center justify-center gap-2 font-bold transition-all text-white
                ${currentStep === steps.length - 1 ? 'bg-emerald-600' : 'bg-indigo-600'}
              `}
            >
              {currentStep === steps.length - 1 ? 'Terminer' : 'Étape Suivante'}
              {currentStep !== steps.length - 1 && <ChevronRight className="w-5 h-5" />}
            </button>
          </div>

        </div>

      </div>
    </div>
  );
}
